import { useState, useEffect, useRef, type ReactNode } from "react";

interface LazySectionProps {
  children: ReactNode;
  height?: string;
  className?: string;
  rootMargin?: string;
  onVisible?: () => void;
}

/**
 * Renders children only when the section enters the viewport.
 * Reduces initial DOM node count dramatically.
 */
const LazySection = ({
  children,
  height = "200px",
  className = "",
  rootMargin = "200px",
  onVisible,
}: LazySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (!hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            onVisible?.();
          }
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, onVisible, isVisible]);

  if (isVisible) return <>{children}</>;

  return <div ref={ref} className={className} style={{ minHeight: height, contentVisibility: 'auto', containIntrinsicSize: `auto ${height}` }} />;
};

export default LazySection;
